import React, { useEffect, useRef, useState } from "react";
import { Button, ButtonType, Checkbox, ColumnDefinition, Grid, IconSize, InlineType, Modal, ModalSize } from "@renta-apps/athenaeum-react-components";
import { IPagedList, SortDirection } from "@renta-apps/athenaeum-toolkit";
import { NewsItem } from "@/models/server/NewsItem";
import AcknowledgementInfo from "@/models/server/AcknowledgementInfo";
import GetNewsAcknowledgmentsRequest from "@/models/server/requests/GetNewsAcknowledgmentsRequest";
import NewsCarousel from "@/components/NewsCarousel/NewsCarousel";
import Localizer from "@/localization/Localizer";

import styles from "./NewsEditor.module.scss";
import NewsEditorForm from "@/components/NewsEditor/NewsEditorForm";
import NewsCarouselItem from "@/components/NewsCarousel/NewsCarouselItem";
import {
    deleteNewsItemAsync,
    fetchNewsAsync,
    saveNewsItemAsync,
    fetchNewsActiveAsync,
    fetchNewsAcknowledgmentsAsync
} from "@/providers/NewsProvider";

interface INewsEditorProps {
}

const NewsEditor = (props: INewsEditorProps) => {

    // useRef (stable across renders), not createRef: openReadBy awaits the API call, which
    // re-renders the component - a createRef would be swapped and its .current reset to null,
    // so openAsync() would silently no-op and the modal would never appear.
    const formModalRef = useRef<Modal>(null);
    const readByModalRef = useRef<Modal>(null);

    const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
    const [activeNewsItems, setActiveNewsItems] = useState<NewsItem[]>([]);

    const [selectedNewsItem, setSelectedNewsItem] = useState<NewsItem | null>(null);
    const [readByNewsId, setReadByNewsId] = useState<string | null>(null);

    // No `type` (default ColumnType.Custom) => plain, read-only display cells.
    // ColumnType.Text would render an editable click-to-edit input regardless of `editable`.
    const readByColumns: ColumnDefinition[] = [
        {
            header: Localizer.newsEditorReadByUserLanguageItemName,
            accessor: "userName",
            minWidth: 200,
        },
        {
            header: Localizer.newsEditorReadByReadAtLanguageItemName,
            accessor: (ack: AcknowledgementInfo) => new Date(ack.acknowledgedAt).toLocaleString(),
            minWidth: 160,
        },
    ];

    const fetchNewsList = () => {
        fetchNewsActiveAsync().then((data) => {
            setActiveNewsItems(data);
        });
        fetchNewsAsync().then((data) => {
            setNewsItems(data);
        });
    }

    const openAddForm = (index: number, after: boolean = true): void => {
        const item: NewsItem = {
            id: null,
            title: "",
            description: "",
            enabled: true,
            priority: false,
            acknowledged: false,
            acknowledgedByCount: 0,
            file: null,
            from: new Date(),
            to: null
        };
        setSelectedNewsItem(item);
        formModalRef.current?.openAsync();
    }

    const openEditForm = (item: NewsItem): void => {
        setSelectedNewsItem(item);
        formModalRef.current?.openAsync();
    }

    const openReadBy = async (item: NewsItem): Promise<void> => {
        if (!item.id) {
            return;
        }

        // The Grid inside the modal loads (and paginates) the readers for this news item on demand.
        setReadByNewsId(item.id);
        await readByModalRef.current?.openAsync();
    }

    const fetchReadByAsync = async (
        _sender: unknown,
        pageNumber: number,
        pageSize: number,
        _sortColumnName: string | null,
        _sortDirection: SortDirection | null): Promise<IPagedList<AcknowledgementInfo>> => {

        const request = new GetNewsAcknowledgmentsRequest();
        request.newsId = readByNewsId || "";
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;

        return await fetchNewsAcknowledgmentsAsync(request);
    }

    const submitNewsForm = async (item: NewsItem): Promise<void> => {
        await saveNewsItemAsync(item);
        fetchNewsList();
        await formModalRef.current?.closeAsync();
        setSelectedNewsItem(null);
    }

    const deleteNews = async (item: NewsItem): Promise<void> => {
        await deleteNewsItemAsync(item);
        setSelectedNewsItem(null);
        fetchNewsList();
    }

    useEffect(() => {
        fetchNewsList();
    }, []);

    return (
        <>
            <div className={styles.NewsEditor}>
                <NewsCarousel newsItems={activeNewsItems} />

                <hr />

                <div className={styles.newsEditorOptions}>
                    <Button type={ButtonType.Orange}
                        className={styles.newsEditorAddNewBtn}
                        label={Localizer.newsEditorAddNew}
                        icon={{ name: "plus", size: IconSize.Normal }}
                        onClick={async () => openAddForm(0, false)}
                        id={"newsAddNewButton"}
                    />
                </div>

                <hr />

                <div>
                    {newsItems.map((item, index) =>
                        <div className={styles.newsItemContainer} key={index}>
                            <div className={styles.carouselItemContainer}>
                                <NewsCarouselItem newsItem={item} index={index} />
                            </div>

                            <div className={styles.newsItemOptions}>
                                <div className={styles.editOptions}>
                                    <Checkbox label={Localizer.newsEditorInputEnabled}
                                        className={styles.checkbox}
                                        value={item.enabled}
                                        readonly
                                        inline
                                        inlineType={InlineType.Right}
                                        id={"newsItemEnabled"}
                                    />

                                    <div>
                                        {item.to != null ? (
                                            `${item.from ? item.from.toLocaleDateString() : ""} - ${item.to ? item.to.toLocaleDateString() : ""}`
                                        ) : (
                                            <>
                                                {`${item.from ? item.from.toLocaleDateString() : ""} - `}
                                                <span>&infin;</span>
                                            </>
                                        )}
                                    </div>

                                    <Button type={ButtonType.Orange}
                                        icon={{ name: "edit", size: IconSize.Normal }}
                                        onClick={async () => openEditForm(item)}
                                        id={"newsItemEditButton"}
                                    />

                                    <Button type={ButtonType.Danger}
                                        icon={{ name: "trash", size: IconSize.Normal }}
                                        onClick={async () => deleteNews(item)}
                                        confirm={Localizer.newsEditorDeleteConfirm}
                                        id={"newsItemDeleteButton"}
                                    />

                                    {
                                        (item.priority) &&
                                        <Button type={ButtonType.Default}
                                            icon={{ name: "book-reader", size: IconSize.Normal }}
                                            label={`${Localizer.newsEditorReadByButton} (${item.acknowledgedByCount})`}
                                            onClick={async () => openReadBy(item)}
                                            id={"newsItemReadByButton"}
                                        />
                                    }
                                </div>
                            </div>

                            <hr />
                        </div>
                    )}
                </div>
            </div>
            <Modal
                ref={formModalRef}
                className={styles.newsEditModal}
                id="newsItemEditFormModal"
                size={ModalSize.ExtraLarge}
                title={Localizer.newsEditorPageTitle}
            >
                {selectedNewsItem &&
                    <NewsEditorForm item={selectedNewsItem}
                        onSubmit={(item: NewsItem) => submitNewsForm(item)}
                        onCancel={() => { formModalRef.current?.closeAsync(); setSelectedNewsItem(null); }}
                    />}
            </Modal>

            <Modal
                ref={readByModalRef}
                id="newsReadByModal"
                size={ModalSize.Default}
                title={Localizer.newsEditorReadByTitle}
            >
                {
                    (readByNewsId) &&
                    // key by news id so the grid remounts (and loads page 1) for the item just opened.
                    <Grid key={readByNewsId}
                          id="newsReadByGrid"
                          pagination={10}
                          minWidth="auto"
                          columns={readByColumns}
                          noDataText={Localizer.newsEditorReadByEmpty}
                          fetchData={fetchReadByAsync}
                    />
                }
            </Modal>
        </>
    );
}

export default NewsEditor;